/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
    Form as FormikForm,
    Masks,
    DatePickerField,
    AutocompleteField,
    InputField,
    CheckboxField,
    TextareaField,
} from "components/common_components/Formik";
import { Field, useFormikContext } from "formik";
import * as Yup from "yup";
import { AlertTriangle } from "lucide-react";
import { DataSearchDialog } from "components/common_components/Dialogs";
import CustomerEntity from "entities/Customer";
import * as EntityUtils from "entities/utils";
import CustomersContext from "./CustomersContext";
import ProfileCard from "components/common_components/ProfileCard";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import Status from "components/common_components/Status";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { hasElements } from "utils/arrays";
import LoadingPage from "components/common_components/LoadingPage";
import { Tenant } from "utils/tenants";
import { dateToString } from "utils/dateUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "components/ui/tabs";
import { Card as UICard, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Badge } from "components/ui/badge";
import {
    User,
    Phone,
    Mail,
    MapPin,
    Calendar,
    FileText,
    Send,
    ExternalLink,
    MessageCircle,
    Search,
    Pencil,
    ArrowLeft,
} from "lucide-react";

function Form({ customer, resources, mode, onEdit, chatComponent, ...rest }) {
    const readOnly = mode === "detail";
    const dispatch = useDispatch();
    const { countries } = resources;
    const [initialValues, setInitialValues] = useState(undefined);
    const { user } = useSelector((state) => state.Login);
    const [forms, setForms] = useState([]);
    const [isFormsLoading, setIsFormsLoading] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [isInvitationsLoading, setIsInvitationsLoading] = useState(false);
    const [isInviting, setIsInviting] = useState(false);

    const { Visitors, Customers } = useContext(CustomersContext);

    const getForms = () => {
        if (!(customer && customer.id)) return;
        setIsFormsLoading(true);
        Customers.getForms({
            id: customer.id,
            onSuccess: (data) => {
                setForms(data);
                setIsFormsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsFormsLoading(false);
            },
        });
    };

    const getInvitations = () => {
        if (!(customer && customer.id)) return;
        setIsInvitationsLoading(true);
        Customers.getInvitations({
            id: customer.id,
            onSuccess: (data) => {
                setInvitations(data);
                setIsInvitationsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsInvitationsLoading(false);
            },
        });
    };

    const handleInvite = async () => {
        if (!(customer && customer.id)) return;
        setIsInviting(true);
        try {
            await Customers.invite({ id: customer.id });
            dispatch(notifySuccess("Invitation sent successfully!"));
            getInvitations();
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data || "Error sending invitation";
            dispatch(notifyError(message));
        } finally {
            setIsInviting(false);
        }
    };

    function init() {
        setInitialValues(EntityUtils.mapEntity(CustomerEntity, customer));
        getForms();
        getInvitations();
    }

    useEffect(() => {
        init();
    }, [customer]);

    const validationSchema = Yup.object({
        firstName: Yup.string().required("Required field"),
        lastName: Yup.string().required("Required field"),
        homePhone: Yup.string()
            .test("Valid home phone", "Incorrect format. Example 123-456-7890", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true,
            )
            .required("Required field"),
        birthDate: Yup.date().required("Required field"),
        email: Yup.string().notRequired().email("Incorrect email"),
        ssn: Yup.string()
            .test("Valid SSN", "Incorrect format. Example 123-45-6789", (value) => (value ? /^\d\d\d-\d\d-\d\d\d\d/.test(value) : true))
            .notRequired(),
        workPhone: Yup.string()
            .test("Valid work phone", "Incorrect format. Example 123-45-6789", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true,
            )
            .notRequired(),
    });

    function filterVisitorsBuilder(filterValue) {
        return filterValue
            ? {
                  firstName: filterValue,
                  lastName: filterValue,
                  homePhone: filterValue,
              }
            : {};
    }

    const [searchVisitorDialogProps, setSearchVisitorDialogProps] = useState({
        open: false,
        title: "Select a visitor",
        onClose: () => setSearchVisitorDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        onSecondaryClick: undefined,
        tableProps: {
            defaultOrder: { order: "asc", orderBy: "firstName" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: Visitors.getListAsync,
            filterBuilder: filterVisitorsBuilder,
        },
    });

    function setCustomerFromVisitor(selectedItems, setValues) {
        const selectedVisitor = selectedItems[0];
        if (selectedVisitor) {
            setValues({
                ssn: selectedVisitor.ssn,
                firstName: selectedVisitor.firstName,
                middleName: selectedVisitor.middleName,
                lastName: selectedVisitor.lastName,
                address: selectedVisitor.address1,
                country: selectedVisitor.country || null,
                city: selectedVisitor.city,
                state: selectedVisitor.state,
                zipCode: selectedVisitor.zipCode,
                homePhone: selectedVisitor.homePhone,
                workPhone: selectedVisitor.workPhone,
                notes: selectedVisitor.notes,
                email: "",
                birthDate: selectedVisitor.dob || null,
                birthPlace: "",
                referredBy: "",
            });
        }
    }

    function searchVisitor({ setValues }) {
        const headCells = [
            { index: 1, id: "firstName", disablePadding: false, label: "First Name" },
            { index: 2, id: "lastName", disablePadding: false, label: "Last Name" },
            { index: 3, id: "homePhone", disablePadding: true, label: "Home Phone" },
            { index: 4, id: "dobString", disablePadding: true, label: "Date of Birth" },
            { index: 5, id: "visitDateString", disablePadding: true, label: "Visit Date" },
        ];
        setSearchVisitorDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setCustomerFromVisitor(selectedItems, setValues),
            tableProps: {
                headCells,
                ...prev.tableProps,
            },
        }));
    }

    const showFormsTab = Tenant.getFeatures().forms.enabled;

    // Map each field to its tab
    const FIELD_TO_TAB = {
        ssn: "personal",
        firstName: "personal",
        middleName: "personal",
        lastName: "personal",
        birthDate: "personal",
        birthPlace: "personal",
        enabled: "personal",
        country: "personal",
        state: "personal",
        city: "personal",
        zipCode: "personal",
        address: "personal",
        referredBy: "personal",
        notes: "personal",
        homePhone: "contact",
        workPhone: "contact",
        email: "contact",
    };

    const TAB_LABELS = {
        personal: "Personal Info",
        contact: "Contact",
    };

    return (
        <>
            {initialValues && (
                <FormikForm
                    validateOnChange={false}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    showButtons={!readOnly}
                    {...rest}
                >
                    {(formikProps) => (
                        <FormContent
                            formikProps={formikProps}
                            readOnly={readOnly}
                            mode={mode}
                            customer={customer}
                            countries={countries}
                            user={user}
                            forms={forms}
                            isFormsLoading={isFormsLoading}
                            invitations={invitations}
                            isInvitationsLoading={isInvitationsLoading}
                            isInviting={isInviting}
                            handleInvite={handleInvite}
                            searchVisitor={searchVisitor}
                            showFormsTab={showFormsTab}
                            chatComponent={chatComponent}
                            onEdit={onEdit}
                            onCancel={rest.onCancel}
                            fieldToTab={FIELD_TO_TAB}
                            tabLabels={TAB_LABELS}
                        />
                    )}
                </FormikForm>
            )}
            <DataSearchDialog {...searchVisitorDialogProps} />
        </>
    );
}

function FormContent({
    readOnly,
    mode,
    customer,
    countries,
    user,
    forms,
    isFormsLoading,
    invitations,
    isInvitationsLoading,
    isInviting,
    handleInvite,
    searchVisitor,
    showFormsTab,
    chatComponent,
    onEdit,
    onCancel,
    fieldToTab,
    tabLabels,
}) {
    const { errors, submitCount } = useFormikContext();
    const [activeTab, setActiveTab] = useState("personal");
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    // Compute which tabs have errors
    const errorFields = Object.keys(errors);
    const tabsWithErrors = {};
    errorFields.forEach((field) => {
        const tab = fieldToTab[field];
        if (tab) {
            if (!tabsWithErrors[tab]) tabsWithErrors[tab] = [];
            tabsWithErrors[tab].push(field);
        }
    });

    const tabHasError = (tabValue) => hasAttemptedSubmit && Boolean(tabsWithErrors[tabValue]);

    // On new submit attempt with errors, navigate to first errored tab
    useEffect(() => {
        if (submitCount > 0) {
            setHasAttemptedSubmit(true);
            if (errorFields.length > 0) {
                const tabOrder = ["personal", "contact"];
                const firstErrorTab = tabOrder.find((t) => tabsWithErrors[t]);
                if (firstErrorTab && firstErrorTab !== activeTab) {
                    setActiveTab(firstErrorTab);
                }
            }
        }
    }, [submitCount]);

    // Build error summary message
    const errorSummaryTabs = Object.keys(tabsWithErrors).map((tab) => tabLabels[tab]).filter(Boolean);

    return (
        <div className="tw-w-full">
            {/* Error banner */}
            {hasAttemptedSubmit && errorFields.length > 0 && (
                <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-mb-5 tw-rounded-lg tw-bg-status-error tw-border tw-border-red-200">
                    <AlertTriangle size={16} className="tw-text-status-error-text tw-flex-shrink-0" />
                    <p className="tw-text-sm tw-text-status-error-text tw-m-0">
                        Please fix {errorFields.length} {errorFields.length === 1 ? "error" : "errors"}
                        {errorSummaryTabs.length > 0 && (
                            <> in: <strong>{errorSummaryTabs.join(", ")}</strong></>
                        )}
                    </p>
                </div>
            )}

            <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-6">
                {/* ── Left sidebar: Profile ── */}
                <div className="tw-flex-shrink-0 lg:tw-w-[260px]">
                    <UICard className="tw-overflow-hidden tw-border-0 tw-shadow-elevated">
                        <div
                            className="tw-h-20 tw-relative"
                            style={{ background: "linear-gradient(135deg, #1e2733 0%, #244068 100%)" }}
                        >
                            <div
                                className="tw-absolute tw-inset-0 tw-opacity-20"
                                style={{
                                    backgroundImage:
                                        "radial-gradient(circle at 20% 50%, #0ec1bc 0%, transparent 50%), radial-gradient(circle at 80% 50%, #f25894 0%, transparent 50%)",
                                }}
                            />
                        </div>
                        <div className="tw-px-5 tw-pb-5 tw--mt-10 tw-flex tw-flex-col tw-items-center tw-relative">
                            <div className="tw-rounded-full tw-border-[3px] tw-border-white tw-shadow-elevated tw-bg-white tw-overflow-hidden">
                                <Field name="photo" type="file">
                                    {({ field, form }) => (
                                        <ProfileCard
                                            {...field}
                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                            readonly={readOnly}
                                        />
                                    )}
                                </Field>
                            </div>
                            {customer && customer.fullName && (
                                <div className="tw-text-center tw-mt-4">
                                    <p className="tw-font-semibold tw-text-brand-dark tw-text-base tw-m-0 tw-leading-snug">
                                        {customer.fullName}
                                    </p>
                                    {customer.email && (
                                        <p className="tw-text-xs tw-text-neutral-400 tw-m-0 tw-mt-1.5 tw-break-all">
                                            {customer.email}
                                        </p>
                                    )}
                                </div>
                            )}
                            {readOnly && user.isAdmin && customer && (
                                <div className="tw-mt-3">
                                    <Badge variant={customer.enabled ? "active" : "inactive"}>
                                        {customer.enabled ? "Active" : "Inactive"}
                                    </Badge>
                                </div>
                            )}
                            {mode === "add" && Tenant.getFeatures().visitors.enabled && (
                                <div className="tw-mt-4 tw-w-full">
                                    <Field>
                                        {({ _, form }) => (
                                            <Button secondary outline small onClick={() => searchVisitor(form)}>
                                                <Search size={14} />
                                                <span>From a Visitor</span>
                                            </Button>
                                        )}
                                    </Field>
                                </div>
                            )}
                        </div>
                    </UICard>

                    {readOnly && customer && (
                        <UICard className="tw-mt-4 tw-border-0 tw-shadow-elevated">
                            <CardContent className="tw-py-4 tw-px-5">
                                <p className="tw-text-xs tw-font-semibold tw-text-neutral-400 tw-uppercase tw-tracking-wider tw-m-0 tw-mb-3">
                                    Quick Info
                                </p>
                                <ul className="tw-list-none tw-p-0 tw-m-0 tw-space-y-3.5">
                                    {customer.homePhone && (
                                        <li className="tw-flex tw-items-center tw-gap-3">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-brand-teal/10 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Phone size={14} className="tw-text-brand-teal" />
                                            </div>
                                            <span className="tw-text-sm tw-text-brand-dark">{customer.homePhone}</span>
                                        </li>
                                    )}
                                    {customer.email && (
                                        <li className="tw-flex tw-items-center tw-gap-3">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-brand-pink/10 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Mail size={14} className="tw-text-brand-pink" />
                                            </div>
                                            <span className="tw-text-sm tw-text-brand-dark tw-break-all">{customer.email}</span>
                                        </li>
                                    )}
                                    {customer.birthDateString && (
                                        <li className="tw-flex tw-items-center tw-gap-3">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-brand-violet/10 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <Calendar size={14} className="tw-text-brand-violet" />
                                            </div>
                                            <span className="tw-text-sm tw-text-brand-dark">{customer.birthDateString}</span>
                                        </li>
                                    )}
                                    {(customer.city || customer.state) && (
                                        <li className="tw-flex tw-items-center tw-gap-3">
                                            <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-brand-green/10 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                                <MapPin size={14} className="tw-text-brand-green" />
                                            </div>
                                            <span className="tw-text-sm tw-text-brand-dark">
                                                {[customer.city, customer.state].filter(Boolean).join(", ")}
                                            </span>
                                        </li>
                                    )}
                                </ul>
                            </CardContent>
                        </UICard>
                    )}
                </div>

                {/* ── Main content ── */}
                <div className="tw-flex-1 tw-min-w-0">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="tw-w-full">
                        <TabsList>
                            <TabsTrigger value="personal" hasError={tabHasError("personal")}>
                                <User size={15} />
                                Personal Info
                                {tabHasError("personal") && (
                                    <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="contact" hasError={tabHasError("contact")}>
                                <Phone size={15} />
                                Contact
                                {tabHasError("contact") && (
                                    <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
                                )}
                            </TabsTrigger>
                            {readOnly && showFormsTab && (
                                <TabsTrigger value="forms">
                                    <FileText size={15} />
                                    Forms
                                    {hasElements(forms) && (
                                        <Badge variant="count">{forms.length}</Badge>
                                    )}
                                </TabsTrigger>
                            )}
                            {readOnly && (
                                <TabsTrigger value="portal">
                                    <Send size={15} />
                                    Portal
                                    {hasElements(invitations) && (
                                        <Badge variant="count">{invitations.length}</Badge>
                                    )}
                                </TabsTrigger>
                            )}
                            {readOnly && chatComponent && (
                                <TabsTrigger value="chat">
                                    <MessageCircle size={15} />
                                    Chat
                                </TabsTrigger>
                            )}
                        </TabsList>

                        {/* ── Personal Info ── */}
                        <TabsContent value="personal" forceMount>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5">
                                <UICard>
                                    <CardHeader>
                                        <CardTitle>General Information</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <InputField disabled={readOnly} fieldName="ssn" label="SSN" lowercase mask={Masks.ssn} light compact />
                                        <InputField disabled={readOnly} fieldName="firstName" label="First Name *" light compact />
                                        <InputField disabled={readOnly} fieldName="middleName" label="Middle Name" light compact />
                                        <InputField disabled={readOnly} fieldName="lastName" label="Last Name *" light compact />
                                        <DatePickerField disabled={readOnly} fieldName="birthDate" label="Date of Birth *" />
                                        <InputField disabled={readOnly} fieldName="birthPlace" label="Place of Birth" light compact />
                                        {user.isAdmin && <CheckboxField readOnly={readOnly} fieldName="enabled" label="Is Enabled" />}
                                    </CardContent>
                                </UICard>

                                <UICard>
                                    <CardHeader>
                                        <CardTitle>Location</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <AutocompleteField
                                            disabled={readOnly}
                                            fieldName="country"
                                            label="Country"
                                            options={countries}
                                            valueKey="code"
                                            labelKey="name"
                                        />
                                        <InputField disabled={readOnly} fieldName="state" label="State" light compact />
                                        <InputField disabled={readOnly} fieldName="city" label="City" light compact />
                                        <InputField disabled={readOnly} fieldName="zipCode" label="Zip Code" light compact />
                                        <InputField disabled={readOnly} fieldName="address" label="Address" light compact />
                                    </CardContent>
                                </UICard>

                                <UICard className="md:tw-col-span-2">
                                    <CardHeader>
                                        <CardTitle>Additional Details</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-5">
                                            <InputField disabled={readOnly} fieldName="referredBy" label="Referred By" light compact />
                                            <div />
                                            <div className="md:tw-col-span-2">
                                                <TextareaField
                                                    disabled={readOnly}
                                                    fieldName="notes"
                                                    label="Notes"
                                                    fullWidth
                                                    maxLength={500}
                                                    rows={4}
                                                />
                                            </div>
                                        </div>
                                    </CardContent>
                                </UICard>
                            </div>
                        </TabsContent>

                        {/* ── Contact ── */}
                        <TabsContent value="contact" forceMount>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5">
                                <UICard>
                                    <CardHeader>
                                        <CardTitle>Phone Numbers</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <InputField
                                            disabled={readOnly}
                                            fieldName="homePhone"
                                            label={
                                                <>
                                                    {"Home Phone * "}
                                                    <Icon
                                                        name="question"
                                                        color="secondary"
                                                        size="xs"
                                                        title="Used for SMS notification purposes."
                                                        style={{ cursor: "help" }}
                                                    />
                                                </>
                                            }
                                            mask={Masks.phoneNumber}
                                            light
                                            compact
                                        />
                                        <InputField
                                            disabled={readOnly}
                                            fieldName="workPhone"
                                            label="Work Phone"
                                            mask={Masks.phoneNumber}
                                            light
                                            compact
                                        />
                                    </CardContent>
                                </UICard>

                                <UICard>
                                    <CardHeader>
                                        <CardTitle>Email</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <InputField
                                            disabled={readOnly}
                                            fieldName="email"
                                            label={
                                                <>
                                                    {"Email "}
                                                    <Icon
                                                        name="question"
                                                        color="secondary"
                                                        size="xs"
                                                        title="Used for notification purposes."
                                                        style={{ cursor: "help" }}
                                                    />
                                                </>
                                            }
                                            helperText="e.g myname@domain.com"
                                            lowercase
                                            light
                                            compact
                                        />
                                    </CardContent>
                                </UICard>
                            </div>
                        </TabsContent>

                        {/* ── Forms ── */}
                        {readOnly && showFormsTab && (
                            <TabsContent value="forms">
                                {isFormsLoading && <LoadingPage />}
                                {!isFormsLoading && hasElements(forms) && (
                                    <div className="tw-space-y-2">
                                        {forms.map((f) => (
                                            <UICard
                                                key={f.id}
                                                className="tw-cursor-pointer hover:tw-shadow-card-hover tw-transition-shadow tw-duration-200"
                                                onClick={() => window.open(`/forms/${f.id}`, "_blank")}
                                            >
                                                <CardContent className="tw-py-3 tw-px-5">
                                                    <div className="tw-flex tw-items-center tw-justify-between">
                                                        <div className="tw-flex tw-items-center tw-gap-4">
                                                            <span className="tw-font-semibold tw-text-sm tw-text-brand-dark">
                                                                {f.form.code}
                                                            </span>
                                                            <Status id={f.status} withIcon />
                                                            <span className="tw-text-xs tw-text-neutral-400">
                                                                v{f.form.version}
                                                            </span>
                                                            {f.form.isCurrent ? (
                                                                <Badge variant="active">Current</Badge>
                                                            ) : (
                                                                <Badge variant="warning">Old Version</Badge>
                                                            )}
                                                        </div>
                                                        <div className="tw-flex tw-items-center tw-gap-3">
                                                            <span className="tw-text-xs tw-text-neutral-400 tw-hidden sm:tw-inline">
                                                                {dateToString(f.stampModificationDatetime)}
                                                            </span>
                                                            <ExternalLink size={14} className="tw-text-neutral-400" />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </UICard>
                                        ))}
                                    </div>
                                )}
                                {!isFormsLoading && !hasElements(forms) && (
                                    <div className="tw-text-center tw-py-12 tw-text-neutral-400 tw-text-sm">
                                        No forms created yet.
                                    </div>
                                )}
                            </TabsContent>
                        )}

                        {/* ── Portal Invitations ── */}
                        {readOnly && (
                            <TabsContent value="portal">
                                <div className="tw-mb-4">
                                    <Button secondary small onClick={handleInvite} loading={isInviting}>
                                        <Icon name="paper-plane" size="sm" color="white" />
                                        <span>Invite to Portal</span>
                                    </Button>
                                </div>
                                {isInvitationsLoading && <LoadingPage />}
                                {!isInvitationsLoading && hasElements(invitations) && (
                                    <div className="tw-space-y-2">
                                        {invitations.map((inv, index) => (
                                            <UICard key={index}>
                                                <CardContent className="tw-py-3 tw-px-5">
                                                    <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
                                                        <Status id={inv.status.toLowerCase()} withIcon />
                                                        <div className="tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-neutral-400">
                                                            <span>Sent: {dateToString(inv.stampCreationDatetime)}</span>
                                                            <span>Expires: {dateToString(inv.expiresAt)}</span>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </UICard>
                                        ))}
                                    </div>
                                )}
                                {!isInvitationsLoading && !hasElements(invitations) && (
                                    <div className="tw-text-center tw-py-12 tw-text-neutral-400 tw-text-sm">
                                        No invitations sent yet.
                                    </div>
                                )}
                            </TabsContent>
                        )}

                        {/* ── Chat ── */}
                        {readOnly && chatComponent && (
                            <TabsContent value="chat">
                                {chatComponent}
                            </TabsContent>
                        )}
                    </Tabs>
                </div>
            </div>

            {/* ── Action buttons ── */}
            {readOnly && (
                <div className="tw-flex tw-items-center tw-gap-3 tw-mt-6 tw-pt-4 tw-border-t tw-border-neutral-200">
                    <Button secondary outline onClick={onCancel}>
                        <ArrowLeft size={16} />
                        <span>Back</span>
                    </Button>
                    <Button secondary onClick={onEdit}>
                        <Pencil size={16} style={{ color: "white" }} />
                        <span>Edit</span>
                    </Button>
                </div>
            )}
        </div>
    );
}

Form.propTypes = {
    customer: PropTypes.shape(EntityUtils.getEntityTypes(CustomerEntity)),
    resources: PropTypes.shape({
        countries: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
            }),
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
    chatComponent: PropTypes.node,
};

Form.defaultProps = {
    customer: EntityUtils.getEntityDefaults(CustomerEntity),
    resources: {
        countries: [],
    },
    onEdit: () => {},
    chatComponent: null,
};

export default Form;
